import GamesState from './state/GamesState';
import AbstractDispatcher from 'frontools/abstract/AbstractDispatcher';
import Context from 'frontools/core/Context';
import { GameMode, GameManager } from './GameManager';
import Template from 'frontools/data/Template';
import { autobind } from 'frontools/decorators/Autobind';
import { Countdown, CountdownEvent } from './components/Countdown';
import GameTimer from './components/GameTimer';
import ColorUtils from './utils/ColorUtils';
import { Assets, IAssetConfig } from './Assets';
import GameScore  from './components/GameScore';
import NumberUtils from 'frontools/utils/NumberUtils';
import Facebook from 'frontools/social/Facebook';

declare var dataLayer           : any;
declare var window           :any

export default class GameAbstract extends AbstractDispatcher {

    public static instance      : GameAbstract;
    public static elGameWrapper : HTMLElement;

    protected _pixi             : PIXI.Application;
    protected _renderer         : PIXI.SystemRenderer;
    protected _stage            : PIXI.Container;
    protected _assets           : Assets;
    protected _context          : Context                       = Context.getInstance();
    protected _gameMode         : string;
    protected _gameTimer        : GameTimer;
    protected _countdown        : Countdown;
    protected _gameScore        : GameScore;
    protected _resultIndex      : number                       = 0;
    protected _gameState        : GamesState                   = GamesState.instance;
    protected _resolution       : number                       = 1;

    private _view               : HTMLElement;
    private _elBtPlay           : HTMLElement;
    private _elHtml             : HTMLHtmlElement              = document.querySelector('html') as HTMLHtmlElement;
    protected _screenHeight     : number;
    protected _screenWidth      : number;
    public isFBIOS              : boolean;

    protected _config           : IGameConfig                  = {
        name: '',
        time: 20,
        results: [],
        countdown: 2,
        assets: [
            { name: 'gameTimer', url: 'core/sprite/gametimer.png', core: true },
            { name: 'gameTimerMask', url: 'core/sprite/gametimer_mask.png', core: true },
            { name: 'result2', url: 'core/audio/result2.mp3', core: true },
            { name: 'result1', url: 'core/audio/result1.mp3', core: true },
            { name: 'result0', url: 'core/audio/result0.mp3', core: true },
            { name: 'countdown2', url: 'core/audio/countdown2.mp3', core: true },
            { name: 'countdown1', url: 'core/audio/countdown1.mp3', core: true },
            { name: 'countdown0', url: 'core/audio/countdown0.mp3', core: true },
            { name: 'running_out', url: 'core/audio/running_out.mp3', core: true },
        ]
    };


    constructor(config: IGameConfig) {
        super();

        // isFBiOS
        this.isFBIOS = window.navigator.userAgent.indexOf('FBIOS') !== -1;
        if (this.isFBIOS) {
            document.querySelector('html').classList.add('fbios');
        }
        

        // Pseudo singleton
        GameAbstract.instance = this;


        // We concat sub object before spreading
        config.assets = this._config.assets.concat(config.assets);
        config.name = GameManager.getInstance().getName();

        // Config spread
        this._config = {...this._config, ...config};

        // HTMLElement definition
        GameAbstract.elGameWrapper = document.querySelector('[data-js="gameWrapper"]') as HTMLElement;

        // pixi initialisation
        let width = GameAbstract.elGameWrapper.clientWidth,
            height = GameAbstract.elGameWrapper.clientHeight,
            bgColor = window.getComputedStyle(GameAbstract.elGameWrapper).getPropertyValue('background-color');
        this._config.bgColor = Number(ColorUtils.rgb2hex(bgColor));
        
        this._resolution = window.devicePixelRatio > 1 ? 2 : 1;
        this._pixi = new PIXI.Application(width, height, {
            resolution: this._resolution,
            backgroundColor: this._config.bgColor,
            transparent: false,
            antialias: false
        });

        this._renderer = this._pixi.renderer;
        this._stage = this._pixi.stage;
        
        GameAbstract.elGameWrapper.appendChild(this._pixi.view);
        this._pixi.render();
        
        // Game mode update
        this._gameMode = GameMode.TITLE;
        this._elHtml.setAttribute('data-screen', GameMode.TITLE.toLowerCase());

        // Title template render
        let copy = JSON.parse(this._context.getValue('gameData')).intro;
        this._html('tplTitle', {
            slug: this._config.name,
            title: copy['title'],
            text: copy['description']
        });
        this._elBtPlay = document.querySelector('[data-js="btPlay"]') as HTMLElement;

        this._updateRenderer();

        // virtualPageview tracking for title
        dataLayer.push({URI: '/jeu-educatifs/' + this._config.name +'/accueil', event: 'virtualPageview'});

        // Preload
        this._preload();
    };


    /*
     * _html - Prints and anim in an html dom view
     * 
     */
    private _html(id, data, callback?) {
        this._view = new Template(document.querySelector('#'+ id) as HTMLElement, data).htmlAsDOM.firstElementChild as HTMLElement;
        //TweenMax.staggerFrom(this._view.children, .5, {delay: .5, y: 50, alpha: 0, ease: Expo.easeOut}, .1);
        TweenMax.staggerFrom(this._view.children, .5, {x: 10, alpha: 0, ease: Expo.easeOut}, .1);
        GameAbstract.elGameWrapper.appendChild(this._view);

        if (typeof callback === 'function') {
            callback();
        }
    };


    /*
     * _preload - Initialise the preloader
     * 
     */
    private _preload() {
        this._assets = new Assets(this._config.assets, this._pixi.renderer.resolution);
        this._assets.addListener(Assets.PROGRESS, this._onPreloadProgress);
        this._assets.addListener(Assets.COMPLETE, this._onPreloadComplete.bind(this));
    };


    /*
     * _onPreloadProgress - Called each time a file is loaded in the preload
     * @param {event} e - Event
     * @param {number} e.progress - loading progression
     * 
     */
    @autobind
    private _onPreloadProgress(e) {
        let progress = this._elBtPlay.querySelector('[data-js="btPlayProgress"]');        
        TweenMax.to(progress, 1, {y: -e.progress +'%', ease: Expo.easeOut, onComplete: function() {
            //this._elBtPlay.classList.remove('is_loading');
        }.bind(this)});
    };


    /*
     * _onPreloadComplete - Called when the preload has been completed
     * @param {event} e
     * 
     */
    protected _onPreloadComplete() {
        this._assets.removeListener(Assets.PROGRESS, this._onPreloadProgress);
        this._assets.removeListener(Assets.COMPLETE, this._onPreloadComplete);

        this._elBtPlay.addEventListener(GameEvent.CLICK, this._prepare.bind(this));
    };


    /*
     * _prepare - Prepare the game components and then starts the countdown
     * 
     */
    protected _prepare() {
        // Game mode update
        this._gameMode = GameMode.PLAYING;
        this._elHtml.setAttribute('data-screen', GameMode.PLAYING.toLowerCase());

        // Animation out of the title panel
        TweenMax.to(this._view, .3, {scale: .7, alpha: 0, ease: Expo.easeOut, onComplete: function() {
            this._elBtPlay.removeEventListener(GameEvent.CLICK, this._prepare);
            (this._view as HTMLElement).parentNode.removeChild(this._view);
        }.bind(this)});

        // Unbind and view removal
        this._gameTimer = new GameTimer(this._config.time);
        this._gameTimer.addListener(GameTimer.COMPLETE, this._onTimerComplete.bind(this));

        // pixi canvas creation
        let countdown = new Countdown(this._config.countdown);
        countdown.addListener(CountdownEvent.COMPLETE, this._start.bind(this));

        this._gameScore = new GameScore(this._config.startingScore);

        window.addEventListener('resize', this._resize.bind(this));
        window.addEventListener('orientationchange', this._resize.bind(this));
        this._resize();

        // Tracking when clicking on the play button
        dataLayer.push({eventCategory: 'jeux-educatifs', eventAction: 'debut-jeux', eventLabel: this._config.name, event: 'genericGAEvent'});
    };


    /*
     * _start - Starts the game timer
     * 
     */
    protected _start() {
        // this._gameMode = GameMode.PLAYING;

        if (this.isFBIOS) {
            window.scrollTo(0, 0);
        }
        // virtualPageview tracking for game
        dataLayer.push({URI: '/jeu-educatifs/' + this._config.name +'/jeu', event: 'virtualPageview'});

        this._gameTimer.start();        
    };

    
    /*
     * _onTimerComplete - on game timer complete handler
     * @param {event} e - Event
     * 
     */
    protected _onTimerComplete(e?) {
        this._gameState.save({score: this._gameScore.score});
        this._end();
    };


    private _getResult(score: number) {
        let values = ['nul', 'moyen', 'fort'],
            result = 0;
        for (var i = 0, l = this._config.results.length; i < l; i++ ) {
            let test = this._config.results[i],
                min = parseInt(test.split('-')[0]),
                max = parseInt(test.split('-')[1]);

                if (score >= min && score <= max) {
                    result = i;
                    break;
                }
        }
        return {
            index: result,
            value: values[result]
        };
    };


    /*
     * _end - Show end screen after results
     * 
     */
    protected _end() {
        this._gameMode = GameMode.END;
        this._elHtml.setAttribute('data-screen', GameMode.END.toLowerCase());
        
        window.removeEventListener('resize', this._resize.bind(this));

        this._resultIndex = this._getResult(this._gameScore.score).index;
        Assets.sounds['result'+ this._resultIndex].play();

        // virtualPageview tracking for results
        dataLayer.push({
            URI: '/jeu-educatifs/' + this._config.name +'/resultats', 
            event: 'virtualPageview', 
            points: this._gameScore.score, 
            tempsDeJeu: this._config.time,
            performance: this._getResult(this._gameScore.score).value
        });
        
        this._gameScore.destroy();
        this._pixi.destroy(true);
        this._pixi = null;

        TweenMax.to(this._view, .3, {scale: .7, alpha: 0, ease: Expo.easeOut, onComplete: function() {
            // End template render
            let copy = JSON.parse(this._context.getValue('gameData')).end,
                bestScore = this._gameState.get().score;
            var nextGameButton = '';

            if (!!GameManager.getInstance().getNextGame(this._config.name)) {
                nextGameButton = new Template(document.querySelector('#tplEndNextGame') as HTMLElement, {
                    nextGameLink: GameManager.getInstance().getNextGame(this._config.name),
                }).htmlAsString;
            }

            // Nutrients
            let nutrients = copy['nutrients'] as string[],
                nutrientsStr = '';

            for (let i = 0, l = nutrients.length; i < l; i++) {
                nutrientsStr += new Template(document.querySelector('#tplEndNutrient') as HTMLElement, {
                    nutrient: nutrients[i],
                }).htmlAsString;
            }

            this._html('tplEnd', {
                slug: this._config.name,
                score: this._gameScore.score,
                bestScore: this._gameState.get().score,
                nextGameButton: nextGameButton,
                replayClass: !!nextGameButton ? 'bigger' : '',
                description: copy['text'],
                more: copy['more'],
                nutrients: nutrientsStr,    
                resultIndex: this._resultIndex
            }, function() {
                let btShare = document.querySelector('[data-js="btShare"]') as HTMLElement;
                
                var btsDisable = this._view.querySelectorAll('[data-disable="linkDisable"]');

                
                // We add this delay to prevent screen mashing from the user to click the share button while the game end
                setTimeout(function() {
                    btShare.addEventListener('click', this._onClickShare);

                    for(let i=0;i<btsDisable.length; i++){
                        btsDisable[i].classList.remove('disable');
                    }
                }.bind(this), 2000);

                // Result tracking
                let buttons = document.querySelectorAll('[data-button]');
                for (let i = 0, l = buttons.length; i < l; i++) {
                    let bt = buttons[i];
                    bt.addEventListener('click', function() {
                        let action = bt.getAttribute('data-tracking-action');
                        dataLayer.push({eventCategory: 'jeux-educatifs', eventAction: action, eventLabel: this._config.name, event: 'genericGAEvent'});
                    }.bind(this))
                }

            }.bind(this));
        }.bind(this)});

        
    };

    @autobind
    private _onClickShare(e: Event) {
        Facebook.sharer(window.location.href, true);
    };

    @autobind
    private _onClickMore(e: Event) {
        
        
    };


    public get config(): IGameConfig {
        return this._config;
    }


    /*
     * _updateRenderer - Update PIXI canvas size according to .game_wrapper
     * 
     */
    private _updateRenderer() {
        let height = GameAbstract.elGameWrapper.clientHeight,
            width = GameAbstract.elGameWrapper.clientWidth;

        this._pixi.renderer.resize(width, height);

        this._screenWidth = this._pixi.screen.width;
        this._screenHeight = this._pixi.screen.height;
    };


    /**
     * _resize - Resize handler
     * @param {Event} e - Event data
     * 
     */
    protected _resize(e?: Event): void {
        this.dispatch({type: GameEvent.RESIZE, event: e});

        // facebook ios browser ugly patch

        if (this._pixi) {
            if (this.isFBIOS) {
                document.querySelector('html').style.height = window.innerHeight +'px';
            }
            this._updateRenderer();
        } else {
            document.querySelector('html').style.height = '';
        }
    }; 


    public get pixi(): PIXI.Application { return this._pixi; }
    public get stage(): PIXI.Container { return this._stage; }
    public get renderer(): PIXI.SystemRenderer { return this._renderer; }

}

interface IGameConfig {
    name?: string;
    time: number;
    results: string[];
    startingScore?: number;
    countdown?: number;
    assets: IAssetConfig[];
    bgColor?: number;
}

export class GameEvent {
    public static CLICK               : string                      = 'click';
    public static TAP                 : string                      = 'tap';
    public static RESIZE              : string                      = 'resize';
}